import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Field from "@/components/Field";
import EnumSelect from "@/components/EnumSelect";
import AsyncSelect from "@/components/AsyncSelect";
import { Button } from "@/components/ui/button";
import { useIsAdmin } from "@/modules/account/hooks/useIsAdmin";

interface Network {
  [key: string]: unknown;
  id: string;
  name: string;
}
const MODE = "contractor_visits_planned_duration_network_scope";
const IDS = "contractor_visits_planned_duration_network_ids";

export function ProjectPlannedDurationNetworkFields({
  disabled = false,
  networks = [],
  availableNetworks = [],
}: {
  disabled?: boolean;
  networks?: Network[];
  availableNetworks?: Network[];
}) {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { formState } = useFormContext();
  const enabled = useWatch({
    name: "contractor_visits_planned_duration_visible",
  });
  const scope = useWatch({ name: MODE }) ?? "all";
  const ids: string[] = useWatch({ name: IDS }) ?? [];
  const [addedNetworks, setAddedNetworks] = useState<Network[]>([]);
  const [pickerVersion, setPickerVersion] = useState(0);
  const [search, setSearch] = useState("");
  const optionsKey = JSON.stringify(availableNetworks);
  const options = availableNetworks.filter((network) =>
    network.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  const names = new Map(
    [...networks, ...addedNetworks].map((item) => [item.id, item.name]),
  );
  const isDisabled =
    disabled || !isAdmin || formState.disabled || formState.isSubmitting;

  // Remain mounted while the PCT controls are hidden, preserving labels of
  // newly selected dictionary items across off/on and all/selected switches.
  if (!enabled) {
    const error = formState.errors[IDS];
    return error ? (
      <p role="alert" className="text-sm text-destructive">
        {t(String(error.message))}{" "}
        {t("projects.plannedDurationVisibility.enableToEditNetworks")}
      </p>
    ) : null;
  }
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Field
        name={MODE}
        label={t(`projects.fields.${MODE}`)}
        render={({ field }) => (
          <EnumSelect
            {...field}
            value={field.value ?? "all"}
            options={["all", "selected"]}
            translationKey="projects.plannedDurationVisibility.networkScope"
            disabled={isDisabled}
          />
        )}
      />
      {scope === "selected" && (
        <Field
          name={IDS}
          label={t(`projects.fields.${IDS}`)}
          required
          wrapperClassName="lg:col-span-2"
          render={({ field }) => (
            <div ref={field.ref} tabIndex={-1}>
              <AsyncSelect
                key={`${optionsKey}:${pickerVersion}`}
                queryKey={[
                  "projectPlannedDurationNetworks",
                  optionsKey,
                  search,
                ]}
                queryFn={async () => ({
                  items: options,
                  count: options.length,
                })}
                onFilter={setSearch}
                placeholder={t("networkSelect.placeholder")}
                loadingLabel={t("networkSelect.loadingNetworks")}
                noResultsLabel={t("networkSelect.noResults")}
                searchLabel={t("networkSelect.search")}
                name={`${IDS}_add`}
                value=""
                onBlur={field.onBlur}
                disabled={isDisabled || availableNetworks.length === 0}
                fetchOn="open"
                enabled={!isDisabled}
                onChange={(id: string, item?: Network) => {
                  setPickerVersion((value) => value + 1);
                  setSearch("");
                  if (!id || ids.includes(id)) return;
                  if (item?.name)
                    setAddedNetworks((current) => [
                      ...current.filter((network) => network.id !== id),
                      { id, name: item.name },
                    ]);
                  field.onChange([...ids, id]);
                }}
              />
              {availableNetworks.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("projects.plannedDurationVisibility.noAssignedNetworks")}
                </p>
              )}
              <ul className="mt-2 flex flex-wrap gap-2">
                {ids.map((id) => {
                  const name = names.get(id) ?? id;
                  return (
                    <li
                      key={id}
                      className="flex items-center gap-2 rounded border px-2 py-1"
                    >
                      <span>{name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isDisabled}
                        aria-label={t(
                          "projects.plannedDurationVisibility.removeNetwork",
                          { name },
                        )}
                        onClick={() =>
                          field.onChange(ids.filter((value) => value !== id))
                        }
                      >
                        ×
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        />
      )}
    </div>
  );
}
